import type { StationCandidate, WeatherIcon } from "./models";

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Minimal shape of an NWS observation's properties that station selection needs. */
export interface RawObservation {
  timestamp?: string | null;
  temperature?: { value: number | null; qualityControl?: string } | null;
  dewpoint?: { value: number | null } | null;
  relativeHumidity?: { value: number | null } | null;
  windSpeed?: { value: number | null } | null;
  barometricPressure?: { value: number | null } | null;
}

/**
 * Returns null when an observation is usable as "current conditions",
 * otherwise a short human-readable reason it was rejected.
 */
export function rejectObservation(obs: RawObservation | null, now: Date, maxAgeMinutes: number): string | null {
  if (!obs || !obs.timestamp) return "no recent observation";
  const observed = Date.parse(obs.timestamp);
  if (Number.isNaN(observed)) return "unreadable timestamp";
  const ageMin = (now.getTime() - observed) / 60_000;
  if (ageMin > maxAgeMinutes) return `observation ${Math.round(ageMin)} min old`;
  if (ageMin < -15) return "timestamp in the future";
  const t = obs.temperature?.value;
  if (t == null) return "missing temperature";
  // QC flag 'X' = rejected by NWS quality control.
  if (obs.temperature?.qualityControl === "X") return "temperature failed quality control";
  if (t < -70 || t > 60) return "implausible temperature";
  const rh = obs.relativeHumidity?.value;
  if (rh != null && (rh < 0 || rh > 100.5)) return "implausible humidity";
  const dew = obs.dewpoint?.value;
  if (dew != null && dew > t + 1) return "dewpoint above temperature";
  const p = obs.barometricPressure?.value;
  if (p != null && (p < 85_000 || p > 110_000)) return "implausible pressure";
  const ws = obs.windSpeed?.value;
  if (ws != null && (ws < 0 || ws > 400)) return "implausible wind speed";
  return null;
}

export interface StationRef {
  id: string;
  name: string;
  lat: number;
  lon: number;
}

/**
 * Walk stations nearest-first and return the first with a fresh, plausible
 * observation. `fetchObs` is called lazily so we stop requesting as soon as
 * one is accepted. A pinned station is tried first when supplied.
 */
export async function selectStation<O extends RawObservation>(
  stations: StationRef[],
  home: { lat: number; lon: number },
  fetchObs: (stationId: string) => Promise<O | null>,
  opts: { now: Date; maxAgeMinutes: number; maxTries: number; pinnedId?: string | null },
): Promise<{ selected: { station: StationRef; distanceKm: number; obs: O } | null; candidates: StationCandidate[] }> {
  const ranked = stations
    .map((s) => ({ station: s, distanceKm: haversineKm(home.lat, home.lon, s.lat, s.lon) }))
    .sort((a, b) => a.distanceKm - b.distanceKm);
  if (opts.pinnedId) {
    const idx = ranked.findIndex((r) => r.station.id === opts.pinnedId);
    if (idx > 0) ranked.unshift(...ranked.splice(idx, 1));
  }
  const candidates: StationCandidate[] = [];
  for (const r of ranked.slice(0, opts.maxTries)) {
    let obs: O | null = null;
    let reason: string | null;
    try {
      obs = await fetchObs(r.station.id);
      reason = rejectObservation(obs, opts.now, opts.maxAgeMinutes);
    } catch {
      reason = "station request failed";
    }
    const cand: StationCandidate = { id: r.station.id, name: r.station.name, distanceKm: round1(r.distanceKm) };
    if (reason === null && obs) {
      candidates.push(cand);
      return { selected: { station: r.station, distanceKm: r.distanceKm, obs }, candidates };
    }
    candidates.push({ ...cand, rejected: reason ?? "unknown" });
  }
  return { selected: null, candidates };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Map an NWS icon URL (e.g. https://api.weather.gov/icons/land/day/tsra,40?size=medium)
 * to our own icon set, so we never hot-link NWS imagery.
 */
export function iconFromNws(url: string | null | undefined): { icon: WeatherIcon; isDaytime: boolean } {
  if (!url) return { icon: "unknown", isDaytime: true };
  const m = /\/icons\/land\/(day|night)\/([^?]+)/.exec(url);
  if (!m) return { icon: "unknown", isDaytime: true };
  const isDaytime = m[1] === "day";
  // Multi-segment icons ("rain/tsra") describe a transition; the first wins.
  const code = (m[2] ?? "").split("/")[0]!.split(",")[0]!;
  return { icon: ICON_CODES[code] ?? "unknown", isDaytime };
}

const ICON_CODES: Record<string, WeatherIcon> = {
  skc: "clear",
  few: "clear",
  sct: "partly-cloudy",
  bkn: "partly-cloudy",
  ovc: "cloudy",
  wind_skc: "wind",
  wind_few: "wind",
  wind_sct: "wind",
  wind_bkn: "wind",
  wind_ovc: "wind",
  snow: "snow",
  rain_snow: "sleet",
  rain_sleet: "sleet",
  snow_sleet: "sleet",
  fzra: "sleet",
  rain_fzra: "sleet",
  snow_fzra: "sleet",
  sleet: "sleet",
  rain: "rain",
  rain_showers: "showers",
  rain_showers_hi: "showers",
  tsra: "thunder",
  tsra_sct: "thunder",
  tsra_hi: "thunder",
  tornado: "thunder",
  hurricane: "thunder",
  tropical_storm: "thunder",
  dust: "haze",
  smoke: "haze",
  haze: "haze",
  hot: "hot",
  cold: "cold",
  blizzard: "snow",
  fog: "fog",
};

export const cToF = (c: number) => (c * 9) / 5 + 32;
export const kphToMph = (k: number) => k * 0.621371;

export function formatTemp(celsius: number, unit: "F" | "C"): string {
  return `${Math.round(unit === "F" ? cToF(celsius) : celsius)}°`;
}

/** Convert a forecast temperature (already in some unit) to the display unit. */
export function convertTemp(value: number, from: "F" | "C", to: "F" | "C"): number {
  if (from === to) return value;
  return to === "F" ? cToF(value) : ((value - 32) * 5) / 9;
}

export function compassPoint(deg: number | null): string {
  if (deg == null) return "";
  const pts = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return pts[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16]!;
}

/**
 * Pick radar frames for animation: the newest frame plus earlier frames
 * spaced roughly `stepMinutes` apart, oldest first. Only real timestamps
 * from the service are returned — nothing is interpolated or repeated.
 */
export function pickRadarFrames(times: string[], count: number, stepMinutes: number): string[] {
  const sorted = [...new Set(times)].filter((t) => !Number.isNaN(Date.parse(t))).sort();
  if (sorted.length === 0) return [];
  const picked: string[] = [sorted[sorted.length - 1]!];
  let target = Date.parse(picked[0]!) - stepMinutes * 60_000;
  for (let i = sorted.length - 2; i >= 0 && picked.length < count; i--) {
    const t = Date.parse(sorted[i]!);
    if (t <= target) {
      picked.unshift(sorted[i]!);
      target = t - stepMinutes * 60_000;
    }
  }
  return picked;
}
